import { z } from "zod";
import { Decision, IsoDateTime, OperatorEnvironment, PlayerRef } from "../common.ts";
import { ConsoleLocale } from "../ports.ts";
import { LIST_SORTS } from "./lists.ts";
import { Cursor, OperatorFilter, PeriodBound, ReasonFilter } from "./common.ts";

// CSV exports of the Explorer lists (spec explorer-user-list "[FR] Columns and export" and
// "[NFR] Secure export", design D13; `explorer.export`): `POST /exports` answers the CSV at once
// for up to 10,000 rows, otherwise `202` with an export generated by the worker (notification when
// ready); `GET /exports` lists the caller's exports; `GET /exports/{id}/download` re-checks owner,
// operator and permission and redirects (`302`) to a 5-minute presigned URL. Precise coordinates
// only with `location.precise.read` both at request and at download time. Request and download
// are audited (author, filters, rows). Headers follow the requester's locale; codes are never
// translated.

/** What can be exported: the users/devices lists and the transactions of a player, device or IP. */
export const EXPORT_KINDS = [
  "users",
  "devices",
  "user_transactions",
  "device_transactions",
  "ip_transactions",
] as const;
export const ExportKind = z.enum(EXPORT_KINDS).meta({ id: "ExportKind" });
export type ExportKind = z.infer<typeof ExportKind>;

export const EXPORT_STATUSES = ["requested", "running", "ready", "failed", "expired"] as const;
export const ExportStatus = z.enum(EXPORT_STATUSES).meta({ id: "ExportStatus" });
export type ExportStatus = z.infer<typeof ExportStatus>;

/** Up to this many rows the CSV is answered at once; above it the export runs in the background. */
export const MAX_INLINE_EXPORT_ROWS = 10_000;
/** Lifetime of a download URL (seconds). */
export const EXPORT_DOWNLOAD_URL_TTL_SECONDS = 300;
/** A generated file is kept this many days (S3 lifecycle); the export is `expired` afterwards. */
export const EXPORT_FILE_RETENTION_DAYS = 7;

const listFilters = {
  from: PeriodBound.optional(),
  to: PeriodBound.optional(),
  operator_id: OperatorFilter.optional(),
  reason: ReasonFilter.optional(),
  q: z.string().trim().min(1).max(128).optional(),
  sort: z.enum(LIST_SORTS).default("transactions"),
  order: z.enum(["asc", "desc"]).default("desc"),
};

const transactionFilters = {
  from: PeriodBound.optional(),
  to: PeriodBound.optional(),
  decision: Decision.optional(),
};

/** Body of `POST /exports`: the list being exported with the filters shown on screen. */
export const CreateExportRequestV1 = z
  .discriminatedUnion("kind", [
    z
      .object({
        kind: z.literal("users"),
        ...listFilters,
        q_type: z.enum(["player_ref", "device_ref"]).default("player_ref"),
      })
      .strict(),
    z
      .object({
        kind: z.literal("devices"),
        ...listFilters,
        q_type: z.enum(["device_ref", "player_ref"]).default("device_ref"),
      })
      .strict(),
    z
      .object({
        kind: z.literal("user_transactions"),
        ...transactionFilters,
        player_ref: PlayerRef,
        operator_id: z.uuid().optional(),
      })
      .strict(),
    z
      .object({
        kind: z.literal("device_transactions"),
        ...transactionFilters,
        device_ref: z.uuid(),
        operator_id: z.uuid().optional(),
      })
      .strict(),
    z
      .object({
        kind: z.literal("ip_transactions"),
        ...transactionFilters,
        ip: z.union([z.ipv4(), z.ipv6()]),
        operator_id: OperatorFilter.optional(),
      })
      .strict(),
  ])
  .meta({ id: "CreateExportRequestV1" });
export type CreateExportRequestV1 = z.infer<typeof CreateExportRequestV1>;

/** One export of the caller (never the filters' identifiers, never a file link). */
export const ExportV1 = z
  .object({
    export_id: z.uuid(),
    kind: ExportKind,
    environment: OperatorEnvironment,
    operator_ids: z.array(z.uuid()).min(1),
    status: ExportStatus,
    /** Fixed at request time: the file has precise-coordinate columns. */
    includes_precise_location: z.boolean(),
    /** Locale of the CSV headers. */
    locale: ConsoleLocale,
    row_count: z.int().nonnegative().nullable(),
    requested_at: IsoDateTime,
    completed_at: IsoDateTime.nullable(),
    /** When the file stops being downloadable (7 days after it is ready). */
    expires_at: IsoDateTime.nullable(),
  })
  .strict()
  .meta({ id: "ExportV1" });
export type ExportV1 = z.infer<typeof ExportV1>;

export const ExportsQueryV1 = z
  .object({
    cursor: Cursor.optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
  })
  .strict()
  .meta({ id: "ExportsQueryV1" });
export type ExportsQueryV1 = z.infer<typeof ExportsQueryV1>;

export const ExportsResponseV1 = z
  .object({ items: z.array(ExportV1), next_cursor: Cursor.nullable() })
  .strict()
  .meta({ id: "ExportsResponseV1" });
export type ExportsResponseV1 = z.infer<typeof ExportsResponseV1>;

/** No query parameter: the installation's environment is implied (a leftover `environment` is refused). */
export const DownloadExportQueryV1 = z.object({}).strict().meta({ id: "DownloadExportQueryV1" });
export type DownloadExportQueryV1 = z.infer<typeof DownloadExportQueryV1>;

/** Body of the `302` answer of `GET /exports/{id}/download` (the URL is also in `Location`). */
export const ExportDownloadV1 = z
  .object({ url: z.url(), expires_at: IsoDateTime })
  .strict()
  .meta({ id: "ExportDownloadV1" });
export type ExportDownloadV1 = z.infer<typeof ExportDownloadV1>;

// Internal events of `explorer` (outbox → worker): ids only, never filters or identifiers.

const exportEventBase = {
  event_id: z.uuid(),
  occurred_at: IsoDateTime,
  export_id: z.uuid(),
  environment: OperatorEnvironment,
};

/** An export above 10,000 rows was requested: the worker generates it (`GenerateExport`). */
export const ExportRequestedV1 = z.object({ ...exportEventBase, kind: ExportKind }).strict();
export type ExportRequestedV1 = z.infer<typeof ExportRequestedV1>;

/** A background export is ready for download (the requester was notified in-app). */
export const ExportReadyV1 = z
  .object({ ...exportEventBase, row_count: z.int().nonnegative() })
  .strict();
export type ExportReadyV1 = z.infer<typeof ExportReadyV1>;

export const EXPORT_EVENT_TYPES = {
  ExportRequested: ExportRequestedV1,
  ExportReady: ExportReadyV1,
} as const;
export const EXPORT_EVENT_VERSION = 1;
export type ExportEventType = keyof typeof EXPORT_EVENT_TYPES;
